import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Layout, Menu, Button, Drawer, Dropdown, Avatar } from 'antd';
import { 
  MenuOutlined, 
  HomeOutlined, 
  ToolOutlined, 
  GiftOutlined, 
  DashboardOutlined, 
  UserOutlined, 
  LogoutOutlined,
  PhoneOutlined,
  TeamOutlined,
  BulbOutlined
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useHeader } from '../../../contexts/HeaderContext';
import { AuthModal } from '../../auth';
import { theme } from '../../../styles/theme';
import { clearCampaignData } from '../../../utils/utmTracking';
import hspLogoOrange from '../../../images/hsplogo-orange.svg';
import { BookingModal } from '../../common/ui';

const { Header: AntHeader } = Layout;

const ROUTES = {
  home: '/',
  services: '/services',
  about: '/about',
  howWeWork: '/how-we-work',
  referral: '/referral-program',
  dashboard: '/dashboard',
  quote: '/quote',
};

const Header = () => {
  // Force refresh to clear cache
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [authModalVisible, setAuthModalVisible] = useState(false);
  const [bookingModalVisible, setBookingModalVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { headerCollapsed } = useHeader();
  const location = useLocation();

  // Check if screen is mobile size
  useEffect(() => {
    const checkIsMobile = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      console.log('Screen width:', width, 'Is mobile:', width < 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);
  const navigate = useNavigate();
  const { currentUser, userProfile, logout } = useAuth();

  const handleDashboardClick = useCallback(() => {
    if (currentUser) {
      navigate(ROUTES.dashboard);
    } else {
      setAuthModalVisible(true);
    }
  }, [currentUser, navigate, setAuthModalVisible]);

  const handleHomeClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Logo clicked - navigating to home');
    // Clear campaign data when navigating to home
    clearCampaignData();
    localStorage.removeItem('show_campaign_page');
    localStorage.removeItem('campaign_utm_params');
    navigate(ROUTES.home);
  }, [navigate]);

  const handleHomeLinkClick = useCallback((e) => {
    // Clear campaign data when navigating to home
    clearCampaignData();
    localStorage.removeItem('show_campaign_page');
    localStorage.removeItem('campaign_utm_params');
  }, []);

  const baseMenuItems = useMemo(
    () => {
      const items = [
        {
          key: ROUTES.home,
          icon: <HomeOutlined style={{ color: theme.colors.primary }} />,
          label: (
            <Link 
              to={ROUTES.home}
              onClick={handleHomeLinkClick}
              style={{ 
                color: theme.colors.primary, 
                fontFamily: theme.fonts.primary,
                fontSize: '18px',
                fontWeight: '600'
              }}
            >
              Home
            </Link>
          ),
        },
        {
          key: ROUTES.services,
          icon: <ToolOutlined style={{ color: theme.colors.primary }} />,
          label: <Link to={ROUTES.services} style={{ color: theme.colors.primary, fontFamily: theme.fonts.primary, fontSize: '18px', fontWeight: '600' }}>Services</Link>,
        },
        {
          key: ROUTES.howWeWork,
          icon: <BulbOutlined style={{ color: theme.colors.primary }} />,
          label: <Link to={ROUTES.howWeWork} style={{ color: theme.colors.primary, fontFamily: theme.fonts.primary, fontSize: '18px', fontWeight: '600' }}>How We Work</Link>,
        },
        {
          key: ROUTES.about,
          icon: <TeamOutlined style={{ color: theme.colors.primary }} />,
          label: <Link to={ROUTES.about} style={{ color: theme.colors.primary, fontFamily: theme.fonts.primary, fontSize: '18px', fontWeight: '600' }}>About Us</Link>,
        },
      ];

      // Add Dashboard only for authenticated users
      if (currentUser) {
        items.push({
          key: ROUTES.dashboard,
          icon: <DashboardOutlined style={{ color: theme.colors.primary }} />,
          label: (
            <span 
              onClick={handleDashboardClick}
              style={{ 
                color: theme.colors.primary, 
                cursor: 'pointer',
                fontFamily: theme.fonts.primary,
                fontSize: '18px',
                fontWeight: '600'
              }}
            >
              Dashboard
            </span>
          ),
        });
      }

      return items;
    },
    [currentUser, navigate, setAuthModalVisible, handleDashboardClick, handleHomeLinkClick]
  );

  const menuItems = useMemo(() => {
    console.log('Menu items:', baseMenuItems);
    console.log('Current user:', currentUser);
    return baseMenuItems;
  }, [baseMenuItems, currentUser]);

  const activeKey = useMemo(() => {
    const { pathname } = location;
    const candidates = menuItems
      .map(i => i.key)
      .filter(k => typeof k === 'string' && (pathname === k || pathname.startsWith(k + '/')))
      .sort((a, b) => b.length - a.length);
    return candidates[0] || ROUTES.home;
  }, [location, menuItems]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (e) {
      console.error('Logout error:', e);
    }
  };


  return (
    <>
      <style>
        {`
          @media (max-width: 767px) {
            .desktop-menu, .desktop-actions {
              display: none !important;
            }
            .mobile-menu-btn {
              display: flex !important;
            }
          }
          @media (min-width: 768px) {
            .mobile-menu-btn {
              display: none !important;
            }
            /* Increase font size for desktop menu items - Force horizontal layout */
            .desktop-menu .ant-menu {
              width: 100% !important;
              display: block !important;
            }
            .desktop-menu .ant-menu-horizontal {
              display: block !important;
              border-bottom: none !important;
              line-height: 60px !important;
            }
            .desktop-menu .ant-menu-horizontal::before {
              display: none !important;
            }
            .desktop-menu .ant-menu-horizontal::after {
              display: none !important;
            }
            .desktop-menu .ant-menu-horizontal > .ant-menu-item,
            .desktop-menu .ant-menu-horizontal > .ant-menu-submenu {
              display: inline-block !important;
              vertical-align: middle !important;
              float: none !important;
              position: relative !important;
              top: 0 !important;
              margin: 0 4px !important;
              border-bottom: none !important;
            }
            .desktop-menu .ant-menu-item {
              font-size: 18px !important;
              font-weight: 600 !important;
              padding: 0 12px !important;
              height: 60px !important;
              line-height: 60px !important;
              white-space: nowrap !important;
              display: inline-block !important;
              visibility: visible !important;
              opacity: 1 !important;
              float: none !important;
              position: relative !important;
              top: 0 !important;
              border-bottom: none !important;
            }
            .desktop-menu .ant-menu-item-icon {
              margin-right: 6px !important;
              font-size: 16px !important;
            }
            .desktop-menu .ant-menu-item:last-child {
              display: inline-block !important;
              visibility: visible !important;
            }
            .desktop-menu .ant-menu-item:last-child {
              margin-right: 0 !important;
            }
            .desktop-menu .ant-menu-item a {
              font-size: 18px !important;
              font-weight: 600 !important;
              white-space: nowrap !important;
            }
            .desktop-menu .ant-menu-item span {
              font-size: 18px !important;
              font-weight: 600 !important;
              white-space: nowrap !important;
            }
            .desktop-menu .ant-menu-item-selected {
              font-size: 18px !important;
              font-weight: 600 !important;
            }
            .desktop-menu .ant-menu-horizontal {
              overflow: visible !important;
            }
          }
        `}
      </style>
      <AntHeader
        style={{
          backgroundColor: theme.colors.white,
          boxShadow: theme.shadows.lg,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          padding: isMobile ? '0 12px' : '0 24px',
          borderBottom: `1px solid ${theme.colors.borderGray}`,
          height: headerCollapsed ? '60px' : 'auto',
          transition: 'all 0.3s ease',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)'
        }}
      >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          height: '60px',
          backgroundColor: theme.colors.white,
          padding: '0',
          position: 'relative'
        }}
      >
        {/* Left side - Logo and Phone (Desktop) / Book Button (Mobile) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', paddingLeft: isMobile ? '8px' : '16px' }}>
          {!isMobile && (
            <>
              <img 
                src={hspLogoOrange} 
                style={{
                  maxHeight:'3rem',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s ease',
                  userSelect: 'none'
                }}
                onClick={handleHomeClick}
                onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                onMouseLeave={(e) => e.target.style.opacity = '1'}
                alt="HSP Logo"
              />
              <a 
                href="tel:+18885046944"
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  textDecoration: 'none', 
                  cursor: 'pointer',
                  gap: '8px'
                }}
              >
                <PhoneOutlined style={{ 
                  fontSize: '20px', 
                  color: theme.colors.primary,
                  fontWeight: 'bold'
                }} />
                <span style={{ 
                  fontSize: '18px', 
                  fontWeight: '700', 
                  color: theme.colors.primary,
                  fontFamily: theme.fonts.primary
                }}>
                  888.504.6944
                </span>
              </a>
            </>
          )}
          {isMobile && (
            <>
              <img 
                src={hspLogoOrange} 
                style={{
                  maxHeight:'2.5rem',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s ease',
                  userSelect: 'none'
                }}
                onClick={handleHomeClick}
                onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                onMouseLeave={(e) => e.target.style.opacity = '1'}
                alt="HSP Logo"
              />
              <a 
                href="tel:+18885046944"
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  textDecoration: 'none', 
                  cursor: 'pointer',
                  gap: '4px',
                  marginLeft: '8px'
                }}
              >
                <PhoneOutlined style={{ 
                  fontSize: '16px', 
                  color: theme.colors.primary,
                  fontWeight: 'bold'
                }} />
                <span style={{ 
                  fontSize: '14px', 
                  fontWeight: '700', 
                  color: theme.colors.primary,
                  fontFamily: theme.fonts.primary
                }}>
                  888.504.6944
                </span>
              </a>
            </>
          )}
        </div>


        <div className="desktop-menu" style={{ 
          display: isMobile ? 'none' : 'block', 
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'auto',
          maxWidth: 'calc(100% - 500px)',
          minWidth: '600px',
          textAlign: 'center'
        }}>
          <Menu
            mode="horizontal"
            selectedKeys={[activeKey]}
            items={menuItems}
            onClick={({ key }) => {
              if (key === ROUTES.home) {
                handleHomeLinkClick();
              }
            }}
            style={{ 
              border: 'none', 
              background: 'transparent', 
              fontFamily: theme.fonts.primary,
              width: '100%',
              fontSize: '18px',
              fontWeight: '600',
              textAlign: 'center'
            }}
            overflowedIndicator={null} // Disable overflow menu
            triggerSubMenuAction="click"
          />
        </div>

        <div className="desktop-actions" style={{ 
          display: isMobile ? 'none' : 'flex', 
          alignItems: 'center', 
          gap: '1rem', 
          position: 'absolute', 
          right: '16px', 
          zIndex: 1,
          paddingRight: '16px',
          minWidth: '250px',
          maxWidth: '300px',
          justifyContent: 'flex-end'
        }}>
          {currentUser ? (
            <>
              <Button
                type="primary"
                size="large"
                onClick={() => setBookingModalVisible(true)}
                style={{
                  background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                  border: 'none',
                  borderRadius: '16px',
                  height: '40px',
                  padding: '0 24px',
                  fontFamily: theme.fonts.primary,
                  fontWeight: '700',
                  fontSize: '16px',
                  boxShadow: '0 8px 25px rgba(39, 174, 96, 0.4)',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
               BOOK NOW
              </Button>

              <Button 
                type="text" 
                onClick={() => navigate(ROUTES.dashboard)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: theme.fonts.primary }}
              >
                <Avatar size="small" icon={<UserOutlined />} />
                <span>{userProfile?.firstName || 'User'}</span>
              </Button>
            </>
          ) : (
            <>
              <Button type="text" onClick={() => setAuthModalVisible(true)} style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                Sign In
              </Button>
              <Button
                type="primary"
                size="large"
                onClick={() => setBookingModalVisible(true)}
                style={{
                  background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                  border: 'none',
                  borderRadius: '16px',
                  height: '40px',
                  padding: '0 24px',
                  fontFamily: theme.fonts.primary,
                  fontWeight: '700',
                  fontSize: '16px',
                  boxShadow: '0 8px 25px rgba(39, 174, 96, 0.4)',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                BOOK NOW
              </Button>
            </>
          )}
        </div>

        {/* Mobile Book Button and Hamburger Menu - Right side */}
        <div 
          className="mobile-menu-btn" 
          style={{ 
            display: isMobile ? 'flex' : 'none', 
            alignItems: 'center', 
            gap: '8px',
            position: 'absolute', 
            right: '8px', 
            zIndex: 1
          }}
        >
          <Button
            type="primary"
            onClick={() => setBookingModalVisible(true)}
            style={{ 
              background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
              border: 'none',
              borderRadius: '16px',
              height: '36px',
              padding: '0 14px',
              fontFamily: theme.fonts.primary,
              fontWeight: '700',
              fontSize: '11px',
              boxShadow: '0 6px 20px rgba(39, 174, 96, 0.4)',
              transition: 'all 0.3s ease',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              minWidth: '90px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              whiteSpace: 'nowrap'
            }}
          >
            BOOK NOW
          </Button>
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setMobileMenuVisible(true)}
            style={{ 
              color: theme.colors.black, 
              fontSize: '20px', 
              height: '40px', 
              width: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '40px',
              padding: 0
            }}
          />
        </div>
      </div>

      <Drawer
        title={
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            fontFamily: theme.fonts.primary,
            fontWeight: '700',
            color: theme.colors.primary
          }}>
            <PhoneOutlined style={{ fontSize: '18px' }} />
            <span>888.504.6944</span>
          </div>
        }
        placement="right"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        width={280}
        styles={{
          header: {
            backgroundColor: theme.colors.white,
            borderBottom: `1px solid ${theme.colors.borderGray}`,
            padding: '16px 24px'
          },
          body: {
            backgroundColor: theme.colors.white,
            padding: '24px'
          }
        }}
      >
        <Menu 
          mode="vertical" 
          selectedKeys={[activeKey]} 
          items={menuItems} 
          onClick={({ key }) => {
            if (key === ROUTES.home) {
              handleHomeLinkClick();
            }
            setMobileMenuVisible(false);
          }} 
          style={{ 
            border: 'none',
            backgroundColor: 'transparent',
            fontFamily: theme.fonts.primary
          }} 
        />

        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          {currentUser ? (
            <>
              <Button
                type="primary"
                size="large"
                block
                onClick={() => {
                  setBookingModalVisible(true);
                  setMobileMenuVisible(false);
                }}
                style={{
                  background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                  border: 'none',
                  borderRadius: '16px',
                  height: '48px',
                  fontFamily: theme.fonts.primary,
                  fontWeight: '700',
                  fontSize: '16px',
                  boxShadow: '0 8px 25px rgba(39, 174, 96, 0.4)',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                BOOK NOW
              </Button>
              <Button
                type="text"
                block
                onClick={() => {
                  handleDashboardClick();
                  setMobileMenuVisible(false);
                }}
                style={{ 
                  marginTop: '12px', 
                  fontFamily: theme.fonts.primary,
                  fontSize: '16px',
                  fontWeight: '600',
                  borderRadius: '16px',
                  height: '48px',
                  color: theme.colors.primary
                }}
              >
                Dashboard
              </Button>
              <Button 
                type="text" 
                block 
                onClick={() => {
                  handleLogout();
                  setMobileMenuVisible(false);
                }} 
                style={{ 
                  fontFamily: theme.fonts.primary,
                  fontSize: '16px',
                  fontWeight: '600',
                  borderRadius: '16px',
                  height: '48px',
                  color: theme.colors.gray
                }}
              >
                Sign Out
              </Button>
            </>
          ) : (
            <>
              <Button 
                type="primary"
                size="large" 
                block 
                onClick={() => {
                  setAuthModalVisible(true);
                  setMobileMenuVisible(false);
                }} 
                style={{ 
                  background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                  border: 'none',
                  borderRadius: '16px',
                  height: '48px',
                  fontFamily: theme.fonts.primary,
                  fontWeight: '700',
                  fontSize: '16px',
                  boxShadow: '0 8px 25px rgba(39, 174, 96, 0.4)',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '12px'
                }}
              >
                Sign In
              </Button>
              <Button
                type="default"
                size="large"
                block
                onClick={() => {
                  setBookingModalVisible(true);
                  setMobileMenuVisible(false);
                }}
                style={{
                  border: `2px solid ${theme.colors.primary}`,
                  borderRadius: '16px',
                  height: '48px',
                  fontFamily: theme.fonts.primary,
                  fontWeight: '700',
                  fontSize: '16px',
                  color: theme.colors.primary,
                  backgroundColor: 'transparent',
                  transition: 'all 0.3s ease'
                }}
              >
                Get Free Quote
              </Button>
            </>
          )}
        </div>
      </Drawer>

      <AuthModal visible={authModalVisible} onClose={() => setAuthModalVisible(false)} />

      {/* Booking Modal */}
      <BookingModal
        visible={bookingModalVisible}
        onClose={() => setBookingModalVisible(false)}
        source="header_booking"
      />
      </AntHeader>
    </>
  );
};

export default Header;
